import React, { useEffect, useState } from "react";
import dayjs from 'dayjs';
import { useAuth } from './auth/AuthContext';

import '../css/racecards.css'

function HandiCapLine({ handicapTime }) {

    if (handicapTime > 0)
        return <div className="row"><div className="col-1"></div><div className="col-2">Handicap</div><div className="col-3 btn-primary bg-success winner">{handicapTime}seconds</div><div className="col-3"></div></div>;
    else
        return <div></div>;
}

function formatMastersCategoryTag(race, lane) {
    if (!race?.event?.handicapEvent) return '';
    const value = String(lane?.mastersCategory || '').trim().toUpperCase();
    return value ? `Mas${value}` : '';
}

function formatRaceCardTime(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value) return '';

    const hhmmss = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (hhmmss) {
        const hh = String(Number(hhmmss[1] || 0)).padStart(2, '0');
        const mm = hhmmss[2];
        return `${hh}:${mm}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    const hh = String(parsed.getHours()).padStart(2, '0');
    const mm = String(parsed.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}

function formatClubWithCompetitorNo(competitorNo, clubName) {
    const club = String(clubName || '').trim();
    const number = String(competitorNo || '').trim();
    if (!number) return club;
    if (!club) return number;
    if (/^(TBC|BYE)$/i.test(club)) return club;
    if (club.startsWith(`${number} - `)) return club;
    return `${number} - ${club}`;
}

function getLaneDisplayName(lane) {
    const preferredName = lane?.titleFull || lane?.title || lane?.titleAbbreviated || lane?.titleMobile || lane?.titleShort || '';
    return formatClubWithCompetitorNo(lane?.competitorNo, preferredName);
}

const RaceCards = () => {
    const [races, setRaces] = useState([]);
    const [info, setInfo] = useState({});
    const [raceYear, setRaceYear] = useState('');
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const { getToken } = useAuth();

    useEffect(() => {

        const fetchInfo = async () => {
            try {
                const token = getToken();
                const response = await fetch('/api/meeting/current', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) {
                    setInfo({});
                    setRaceYear('');
                    return;
                }
                const json = await response.json();
                setInfo(json && typeof json === 'object' ? json : {});
                const year = dayjs(json?.competitionDate).format('YYYY');
                setRaceYear(year === 'Invalid Date' ? '' : year);
            }
            finally { }
        };

        const fetchData = async () => {
            try {
                const token = getToken();
                const response = await fetch('/api/timetable', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!response.ok) {
                    if (response.status === 403) {
                        setErrorMessage('Race cards are unavailable. This page is locked unless you are signed in with a role.');
                    } else {
                        setErrorMessage(`Failed to load race cards (${response.status}).`);
                    }
                    setRaces([]);
                    return;
                }
                const json = await response.json();
                setRaces(Array.isArray(json) ? json : []);
                setErrorMessage('');

                //window.print();

            } catch (error) {
                console.log("error", error);
                setErrorMessage('Failed to load race cards.');
                setRaces([]);
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
        fetchData();
    }, [getToken]);

    const printableRaces = races.filter(race => Number(race?.event?.id || -1) > 0);

    if (loading) {
        return <div className="p-3">Loading race cards...</div>;
    }

    if (errorMessage) {
        return <div className="p-3">{errorMessage}</div>;
    }

    if (!printableRaces.length) {
        return <div className="p-3">No race cards available for the current timetable.</div>;
    }

    const logoSource = [
        info?.tenantLogoUrl,
        info?.regattaLogoUrl,
        info?.logoUrl,
        info?.logo,
        '/favicon.ico'
    ].map(value => String(value || '').trim()).find(Boolean) || '/favicon.ico';


    return (
        <>{printableRaces.map((filteredRace, index) => (
            <div className="row rc-rowStyle" key={filteredRace?.raceId || filteredRace?.raceNo || index}>
                    <div className="col-10">
                          <div className="row pt-3">
                            <div className="col-1"></div><div className="col-9 regattaName">{info.competitionName} {raceYear }</div>
                          </div>
                          <div className="row pt-4">
                            <div className="col-1"></div><div className="col-2">Race No</div><div className="col-2">{filteredRace.raceNo}</div><div className="col-4 text-end">Next Race: {filteredRace.nextRaceNo}</div>
                          </div>
                          <div className="row">
                                                        <div className="col-1"></div><div className="col-2">Race Time</div><div className="col-6">{formatRaceCardTime(filteredRace?.raceTimeFormatted || filteredRace?.raceTime || '')}</div>
                          </div>
                          <div className="row">
                                                        <div className="col-1"></div><div className="col-2">Event</div><div className="col-9 rc-event-single-line" title={filteredRace?.event?.title || ''}>{filteredRace.event.title }</div>
                          </div>
                          <div className="row">
                        <div className="col-1"></div><div className="col-2">Round</div><div className="col-6">{filteredRace.round }</div>
                    </div>
                    <HandiCapLine handicapTime={filteredRace.handicapTime } />
                    <div className="row pt-4">
                        <div className="col-1"></div><div className="col-11">
                            <div className="row laneTitles">
                                <div className="col-6">Middlesex (Enclosure)</div><div className="col-6">Surrey</div>
                            </div>
                            <div className="row laneTitles mh56 pt-3 pb-3">
                                <div className="col-6">
                                    {getLaneDisplayName(filteredRace?.laneA)}<br />
                                    <span className="rc-crew-name">{filteredRace?.laneA?.suffix}</span>
                                    {formatMastersCategoryTag(filteredRace, filteredRace?.laneA) && (
                                        <span className="rc-masters-category-tag">[{formatMastersCategoryTag(filteredRace, filteredRace?.laneA)}]</span>
                                    )}
                                </div>
                                <div className="col-6">
                                    {getLaneDisplayName(filteredRace?.laneB)}<br />
                                    <span className="rc-crew-name">{filteredRace?.laneB?.suffix}</span>
                                    {formatMastersCategoryTag(filteredRace, filteredRace?.laneB) && (
                                        <span className="rc-masters-category-tag">[{formatMastersCategoryTag(filteredRace, filteredRace?.laneB)}]</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row pt-5 mt-3">
                        <div className="col-1"></div><div className="col-1 mr-4">Winner</div><div className="col-6 bottomLine"></div>
                    </div>
                    <div className="row pt-5">
                        <div className="col-1"></div><div className="col-1 mr-4">Time</div><div className="col-6 bottomLine"></div>
                    </div>
                    <div className="row pt-5">
                        <div className="col-1"></div><div className="col-1 mr-4">Verdict</div><div className="col-6 bottomLine"></div>
                    </div>
                    <div className="row pt-5">
                        <div className="col-1"></div><div className="col-1 mr-4">Umpire</div><div className="col-6 bottomLine"></div>
                    </div>
                </div>
                <div className="col-2 p-0">
                    <img
                        src={logoSource}
                        alt="Regatta Logo"
                        width="150"
                        onError={(event) => {
                            if (event.currentTarget.dataset.fallbackApplied === '1') {
                                event.currentTarget.style.display = 'none';
                                return;
                            }
                            event.currentTarget.dataset.fallbackApplied = '1';
                            event.currentTarget.src = '/favicon.ico';
                        }}
                    />
                </div>
            </div>
        ))}</>
    );
};

export default RaceCards;